// src/pages/student/exams/StudentExamView.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Spinner, Alert, Button, Badge, Row, Col, Form, Modal,
} from "react-bootstrap";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import {
  doc, getDoc, collection, query, where, orderBy, getDocs, limit,
  onSnapshot, setDoc, serverTimestamp
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage, db } from "../../../services/firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

dayjs.extend(durationPlugin);

const TOAST_ID = "student-exam-view";

/* ---------------- helpers ---------------- */
const norm = (s) => String(s || "").trim().toLowerCase();
const qtype = (q) => String(q?.questionType || "").toLowerCase();
const secTitle = (k) => (k ? `Section ${String(k).trim().toUpperCase()}` : "Section");

// descriptive score
const getDescScore = (att = {}) => {
  const v = att.evaluatedScore ?? att.score ?? att.marksObtained ?? att.obtained;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// compute total from questions if not provided
const computeExamTotalFromQuestions = (qs = []) =>
  qs.reduce((sum, q) => {
    const t = qtype(q);
    if (t === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
      return sum + q.caseSubQs.reduce((s, sq) => s + (Number(sq.marks ?? q.marks ?? 1) || 1), 0);
    }
    return sum + (Number(q.marks ?? 1) || 1);
  }, 0);

const getDescTotal = (exam = {}, att = {}, qs = []) => {
  const raw =
    att.totalMarks ??
    exam.totalMarks ??
    exam.maxScore ??
    exam.fullMarks ??
    exam.total ??
    null;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;
  const computed = computeExamTotalFromQuestions(qs);
  return computed > 0 ? computed : null;
};

const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmtShort = (d) => dayjs(d).format("DD MMM • HH:mm");
const fmtHMS = (seconds) => {
  const d = dayjs.duration(seconds, "seconds");
  const hh = String(Math.floor(d.asHours())).padStart(2, "0");
  const mm = String(d.minutes()).padStart(2, "0");
  const ss = String(d.seconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const getWindowState = (e) => {
  const mode = String(e?.examMode || "live").toLowerCase();
  if (mode === "mock") {
    return { state: "live", start: null, end: null, diffSec: 0, label: "Available anytime" };
  }
  if (!e?.date || !e?.startTime || !e?.endTime) {
    return { state: "unknown", start: null, end: null, diffSec: 0, label: "" };
  }
  const start = dayjs(`${e.date} ${e.startTime}`, "YYYY-MM-DD HH:mm", true);
  const rawEnd = dayjs(`${e.date} ${e.endTime}`, "YYYY-MM-DD HH:mm", true);
  if (!start.isValid() || !rawEnd.isValid()) {
    return { state: "unknown", start: null, end: null, diffSec: 0, label: "" };
  }
  let end = rawEnd;
  if (rawEnd.isBefore(start)) end = start.add(e.durationMins || 0, "minute");
  const now = dayjs();
  if (now.isBefore(start)) {
    const diffSec = start.diff(now, "second");
    return { state: "upcoming", start, end, diffSec, label: `Starts in ${fmtHMS(diffSec)}` };
  }
  if (now.isAfter(end)) return { state: "ended", start, end, diffSec: 0, label: "Ended" };
  const diffSec = end.diff(now, "second");
  return { state: "live", start, end, diffSec, label: `Time left ${fmtHMS(diffSec)}` };
};

const hasSubmitted = (att) => ["submitted", "evaluated"].includes(norm(att?.status));
const isEvaluated  = (att) => norm(att?.status) === "evaluated";

const extractIndexUrl = (err) => {
  try {
    const m = String(err?.message || "").match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i);
    return m ? m[0] : "";
  } catch { return ""; }
};

const pickQuestionText = (q, fallback) =>
  q.title || q.prompt || q.question || q.text || q.body || q.description || fallback;

const getSolutionUrl = (exam) =>
  exam?.solutionPdfUrl || exam?.solutionUrl || exam?.solutions?.url || "";

/* -------- group Subject → Section with continuous numbering -------- */
const useGroupedQuestions = (questions) => useMemo(() => {
  let counter = 0;
  const groups = [];
  const getSubj = (q) => (q.subject ? String(q.subject) : "General");
  const getSec  = (q) => String(q.sectionKey ?? "").trim();

  questions.forEach((q) => {
    const subj = getSubj(q);
    const sec  = getSec(q);

    let sg = groups.find((g) => g.subject === subj);
    if (!sg) { sg = { subject: subj, sections: [] }; groups.push(sg); }

    let sc = sg.sections.find((s) => s.key === sec);
    if (!sc) { sc = { key: sec, start: counter + 1, end: null, items: [] }; sg.sections.push(sc); }

    sc.items.push(q);
    counter++;
  });

  groups.forEach((g) => g.sections.forEach((s) => { s.end = s.start + s.items.length - 1; }));
  return { groups, total: counter };
}, [questions]);

/* ---------- Component ---------- */
export default function StudentExamView() {
  const { examId } = useParams();
  const nav = useNavigate();
  const auth = getAuth();
  const uid = auth.currentUser?.uid || null;

  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  const [profile, setProfile] = useState(null);

  const [exam, setExam] = useState(null);
  const [loadingExam, setLoadingExam] = useState(true);

  const [questions, setQuestions] = useState([]);
  const [loadingQs, setLoadingQs] = useState(true);
  const [error, setError] = useState("");
  const [indexUrl, setIndexUrl] = useState("");

  // attempt + ticking for live label
  const [attempt, setAttempt] = useState(null);
  const [loadingAttempt, setLoadingAttempt] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(id); }, []);

  // upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // proctoring
  const [proctorStrikes, setProctorStrikes] = useState(0);
  const [hideQuestions, setHideQuestions] = useState(false);
  const lastProctorEventTsRef = useRef(0);
  const proctorLockedRef = useRef(false);

  // modals
  const [showSolutions, setShowSolutions] = useState(false);
  const [showRemarks, setShowRemarks] = useState(false);

  useEffect(() => { proctorLockedRef.current = hideQuestions; }, [hideQuestions]);

  // time alerts memory
  const firedAlertsRef = useRef(new Set());
  const isAlertOpenRef = useRef(false);
  const questionRef = useRef(null);

  /* load profile */
  useEffect(() => {
    if (!schoolId || !studentId) return;
    const ref = doc(db, "schools", schoolId, "students", studentId);
    const unsub = onSnapshot(ref, (snap) => {
      setProfile(snap.exists() ? ({ id: snap.id, ...snap.data() }) : null);
    });
    return () => unsub();
  }, [schoolId, studentId]);

  const isActive = norm(profile?.status) !== "inactive";

  /* load the exam */
  useEffect(() => {
    if (!schoolId || !examId) { setError("Missing context"); setLoadingExam(false); return; }
    const ref = doc(db, "schools", schoolId, "exams", examId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setExam(null);
        setError("Exam not found");
      } else {
        setExam({ id: snap.id, ...snap.data() });
        setError("");
      }
      setLoadingExam(false);
    }, (e) => {
      setError(e?.message || "Failed to load exam");
      setLoadingExam(false);
    });
    return () => unsub();
  }, [schoolId, examId]);

  /* load attempt (live updates) */
  useEffect(() => {
    if (!schoolId || !examId || !uid) return;
    const ref = doc(db, "schools", schoolId, "exams", examId, "attempts", uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setAttempt(data);
        const strikes = Number(data?.proctor?.strikes || 0);
        setProctorStrikes(strikes);
        const locked = Boolean(data?.proctor?.locked);
        setHideQuestions(locked);
      } else {
        setAttempt(null);
      }
      setLoadingAttempt(false);
    }, () => setLoadingAttempt(false));
    return () => unsub();
  }, [schoolId, examId, uid]);

  /* load questions from multiple locations */
  useEffect(() => {
    if (!exam || !schoolId) return;

    const load = async () => {
      setLoadingQs(true);
      setError("");
      setIndexUrl("");
      const merged = new Map();
      const addRows = (rows, prefix) => {
        rows.forEach((q) => {
          const key = `${prefix}:${q.id || q._id || Math.random().toString(36).slice(2)}`;
          merged.set(key, q);
        });
      };

      const tryCentral = async () => {
        if (!exam.templateId) return;
        try {
          const qsRef = collection(db, "schools", schoolId, "questions");
          const qy = query(
            qsRef,
            where("templateId", "==", exam.templateId),
            orderBy("order", "asc"),
            limit(2000)
          );
          const snap = await getDocs(qy);
          addRows(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })), "central");
        } catch (e) {
          const maybeIndexUrl = extractIndexUrl(e);
          if (maybeIndexUrl) setIndexUrl((u) => u || maybeIndexUrl);
          try {
            const qsRef = collection(db, "schools", schoolId, "questions");
            const qy2 = query(qsRef, where("templateId", "==", exam.templateId), limit(2000));
            const snap2 = await getDocs(qy2);
            addRows(snap2.docs.map(d => ({ id: d.id, ...(d.data() || {}) })), "central");
          } catch { /* ignore */ }
        }
      };

      const tryTemplateSub = async () => {
        if (!exam.templateId) return;
        try {
          const ref = collection(db, "schools", schoolId, "examTemplates", exam.templateId, "questions");
          const snap = await getDocs(ref);
          addRows(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })), "tplsub");
        } catch {}
      };

      const tryExamSub = async () => {
        try {
          const ref = collection(db, "schools", schoolId, "exams", exam.id, "questions");
          const snap = await getDocs(ref);
          addRows(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })), "examsub");
        } catch {}
      };

      try { await Promise.all([tryCentral(), tryTemplateSub(), tryExamSub()]); } catch {}
      const rows = Array.from(merged.values());
      rows.sort((a, b) => {
        const A = Number.isFinite(+a.order) ? +a.order : Number.MAX_SAFE_INTEGER;
        const B = Number.isFinite(+b.order) ? +b.order : Number.MAX_SAFE_INTEGER;
        if (A !== B) return A - B;
        return String(pickQuestionText(a, "")).localeCompare(String(pickQuestionText(b, "")));
      });
      setQuestions(rows);
      setLoadingQs(false);
    };

    load();
  }, [exam, schoolId]);

  /* -------- derived -------- */
  const isDescriptive = useMemo(() => norm(exam?.examType) === "descriptive", [exam?.examType]);
  const ws = useMemo(() => exam ? getWindowState(exam) : { state: "unknown", label: "" }, [exam, tick]);
  const isMock = useMemo(() => norm(exam?.examMode || "live") === "mock", [exam?.examMode]);
  const isLiveExam = !isMock;

  const hasPerQuestionSolutions = useMemo(
    () => (questions || []).some(
      q => q.solutionHtml || q.modelAnswerHtml || q.answerHtml || q.explanationHtml
    ),
    [questions]
  );

  const modelAnswerUrl =
    exam?.modelAnswerPdfUrl ||
    exam?.solutionPdfUrl ||
    exam?.modelAnswersPdf ||
    exam?.solutionsPdf ||
    getSolutionUrl(exam) ||
    "";

  // Any solution present at all?
  const anySolutionAttached = hasPerQuestionSolutions || !!modelAnswerUrl;

  // Button visibility rule:
  // - LIVE: visible only after submission
  // - MOCK/ANYTIME: always visible
  const canShowSolutionsButton = useMemo(() => {
    if (!isDescriptive) return false;
    if (!anySolutionAttached) return false;
    if (isLiveExam) return hasSubmitted(attempt); // live → after upload only
    return true;                                   // mock/anytime → always
  }, [isDescriptive, isLiveExam, attempt, anySolutionAttached]);

  // Actually render inline solutions only if the button is visible AND toggled on
  const canRenderInlineSolutions = canShowSolutionsButton && showSolutions;

  // upload window
  const canUploadWindow = useMemo(() => {
    if (!exam) return false;
    if (isMock) return true;
    return ws.state === "live"; // strictly on-time for live
  }, [exam, ws.state, isMock]);

  const canReplace = useMemo(
    () => Boolean(exam?.allowMultipleAttempts && hasSubmitted(attempt)),
    [exam, attempt]
  );

  const policyBlocked = useMemo(() => {
    if (!attempt) return false;
    if (hasSubmitted(attempt) && !exam?.allowMultipleAttempts) return true;
    return false;
  }, [exam, attempt]);

  const onViewSolutionsTabToggle = () => setShowSolutions(v => !v);

  const ensureAttemptStart = async () => {
    if (!schoolId || !exam || !uid) return;
    try {
      const ref = doc(db, "schools", schoolId, "exams", exam.id, "attempts", uid);
      const snap = await getDoc(ref);
      const base = {
        uid,
        schoolId,
        examId: exam.id,
        byStudent: { id: studentId || "", name: studentName || "" },
        status: "in-progress",
        startedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (!snap.exists()) {
        await setDoc(ref, { ...base, createdAt: serverTimestamp() }, { merge: true });
      } else {
        const a = snap.data();
        if (hasSubmitted(a) && exam?.allowMultipleAttempts) {
          await setDoc(ref, { ...base }, { merge: true });
        } else if (norm(a.status) !== "in-progress") {
          await setDoc(ref, base, { merge: true });
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't start attempt. Try again.", { containerId: TOAST_ID });
      throw e;
    }
  };

  const onChooseFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setFile(null);
    if (f.type !== "application/pdf") {
      toast.warn("Please select a PDF file.", { containerId: TOAST_ID });
      return setFile(null);
    }
    const MAX = (exam?.maxUploadMB || 15) * 1024 * 1024;
    if (f.size > MAX) {
      toast.warn(`File too large. Max ${(exam?.maxUploadMB || 15)} MB.`, { containerId: TOAST_ID });
      return setFile(null);
    }
    setFile(f);
  };

  const doUpload = async () => {
    if (!schoolId || !exam || !uid || !file) return;
    try {
      if (!isActive) {
        toast.warn("Your account is inactive. Upload is disabled.", { containerId: TOAST_ID });
        return;
      }
      if (!canUploadWindow) {
        toast.warn("Upload window is closed or not started.", { containerId: TOAST_ID });
        return;
      }
      if (policyBlocked && !canReplace) {
        toast.warn("You have already submitted. Retake not allowed.", { containerId: TOAST_ID });
        return;
      }

      setUploading(true);
      await ensureAttemptStart();

      const path = `schools/${schoolId}/exams/${exam.id}/answers/${uid}/${Date.now()}_${file.name.replace(/[^\w.\-]+/g, "_")}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file, { contentType: "application/pdf" });
      const url = await getDownloadURL(sref);

      const attRef = doc(db, "schools", schoolId, "exams", exam.id, "attempts", uid);
      await setDoc(attRef, {
        status: "submitted",
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        upload: { url, name: file.name, size: file.size, contentType: "application/pdf", path }
      }, { merge: true });

      setFile(null);
      toast.success("Answer sheet uploaded.", { containerId: TOAST_ID });
    } catch (e) {
      console.error(e);
      toast.error("Upload failed. Please try again.", { containerId: TOAST_ID });
    } finally {
      setUploading(false);
    }
  };

  /* -------- persist proctor state -------- */
  const recordProctorState = async (nextStrikes, reason) => {
    if (!schoolId || !exam || !uid) return;
    const attRef = doc(db, "schools", schoolId, "exams", exam.id, "attempts", uid);
    try {
      await setDoc(attRef, {
        proctor: {
          strikes: nextStrikes,
          locked: nextStrikes >= 3,
          lastReason: reason || "",
          lastAt: serverTimestamp(),
          ack: attempt?.proctor?.ack || false
        }
      }, { merge: true });
    } catch (e) {
      console.error("Failed to persist proctor state", e);
    }
  };

  /* -------- one-time proctoring notice (LIVE) -------- */
  useEffect(() => {
    if (!exam || isMock) return;
    if (ws.state !== "live") return;
    if (attempt?.proctor?.ack) return;

    (async () => {
      await Swal.fire({
        icon: "info",
        title: "Proctoring in effect",
        html: "This live exam monitors <b>tab switching</b> and <b>window focus</b>. Switching away 3 times will <b>hide questions</b>.",
        confirmButtonText: "OK, continue",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      await recordProctorState(proctorStrikes, "acknowledged");
      const attRef = doc(db, "schools", schoolId, "exams", exam.id, "attempts", uid);
      await setDoc(attRef, { proctor: { ...(attempt?.proctor || {}), ack: true, ackAt: serverTimestamp() } }, { merge: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam, isMock, ws.state, attempt?.proctor?.ack]);

  /* -------- leave guard (Back/refresh) for descriptive until upload -------- */
  const shouldWarnLeave = isDescriptive && isActive && !hasSubmitted(attempt);

  useEffect(() => {
    if (!shouldWarnLeave) return;

    const pushTrap = () => window.history.pushState({ guard: true }, "", window.location.href);
    pushTrap();
    let handling = false;

    const onPopState = async () => {
      if (handling) return;
      handling = true;

      const res = await Swal.fire({
        icon: "warning",
        title: "Leave without uploading?",
        text: "You haven’t uploaded your answer PDF yet. If you leave, you may lose access to the paper during the live window.",
        showCancelButton: true,
        confirmButtonText: "Leave",
        cancelButtonText: "Stay",
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (res.isConfirmed) {
        window.removeEventListener("popstate", onPopState);
        window.history.back();
      } else {
        pushTrap();
      }

      handling = false;
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [shouldWarnLeave]);

  useEffect(() => {
    if (!shouldWarnLeave) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [shouldWarnLeave]);

  const onBack = async () => {
    if (shouldWarnLeave) {
      const res = await Swal.fire({
        icon: "warning",
        title: "Leave without uploading?",
        text: "You haven’t uploaded your answer PDF yet.",
        showCancelButton: true,
        confirmButtonText: "Leave",
        cancelButtonText: "Stay",
        reverseButtons: true,
      });
      if (!res.isConfirmed) return;
    }
    nav(-1);
  };

  /* -------- proctoring: tab switching (LIVE only) -------- */
  useEffect(() => {
    if (!exam || isMock || ws.state !== "live") return;
    if (hideQuestions) return;

    const THROTTLE_MS = 1500;

    const sweetWarn = async (title, text, isFinal = false) => {
      if (isAlertOpenRef.current) return;
      isAlertOpenRef.current = true;
      try {
        await Swal.fire({
          icon: isFinal ? "error" : "warning",
          title,
          text,
          confirmButtonText: isFinal ? "OK" : "OK, continue",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
      } finally {
        isAlertOpenRef.current = false;
      }
    };

    const bump = async (reason) => {
      if (proctorLockedRef.current) return;
      const now = Date.now();
      if (now - lastProctorEventTsRef.current < THROTTLE_MS) return;
      lastProctorEventTsRef.current = now;

      const base = Math.max(proctorStrikes, Number(attempt?.proctor?.strikes || 0));
      const next = base + 1;

      if (next < 3) {
        await sweetWarn("Tab switching detected", `This is warning ${next}/3. Please stay on the exam page.`);
      } else {
        await sweetWarn("Proctoring lock", "You switched tabs or lost focus too many times. Questions will now be hidden.", true);
      }

      setProctorStrikes(next);
      if (next >= 3) {
        setHideQuestions(true);
        proctorLockedRef.current = true;
      }
      recordProctorState(next, reason);
    };

    const onVis  = () => { if (document.hidden) bump("visibility"); };
    const onBlur = () => bump("blur");

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam, ws.state, isMock, hideQuestions, attempt?.proctor?.strikes, proctorStrikes]);

  /* -------- time-left toast alerts (LIVE only) -------- */
  useEffect(() => {
    if (!exam || isMock || ws.state !== "live") return;
    const rem = ws.diffSec || 0;
    const points = [1800, 900, 300, 60];
    points.forEach((p) => {
      if (rem <= p && !firedAlertsRef.current.has(p)) {
        firedAlertsRef.current.add(p);
        const mins = Math.max(1, Math.round(p / 60));
        toast.info(`${mins} minute${mins === 1 ? "" : "s"} remaining`, { containerId: TOAST_ID });
      }
    });
    if (rem <= 0 && !firedAlertsRef.current.has(0)) {
      firedAlertsRef.current.add(0);
      toast.error("Time up.", { containerId: TOAST_ID });
    }
  }, [ws.diffSec, ws.state, isMock, exam]);

  /* -------- grouping for render -------- */
  const { groups: grouped } = useGroupedQuestions(questions);
const gradeOf = (p) => {
  if (p == null) return null;
  return p >= 90 ? "A+" :
         p >= 80 ? "A"  :
         p >= 70 ? "B"  :
         p >= 60 ? "C"  :
         p >= 50 ? "D"  : "F";
};
  /* ------------- render ------------- */
  if (loadingExam) return <div className="text-center py-5"><Spinner /></div>;

  if (error) {
    return (
      <div className="p-3">
        <Alert variant="danger">
          <div className="mb-1">{error}</div>
          {indexUrl && (
            <div className="mt-1">
              This query may need a composite index.{" "}
              <a href={indexUrl} target="_blank" rel="noreferrer">Create it in Firestore</a>, then refresh this page.
            </div>
          )}
        </Alert>
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="p-3">
        <Alert variant="warning">Exam not found.</Alert>
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
      </div>
    );
  }

  const isAnytime = isMock;
  const isLiveNow = !isAnytime && ws.state === "live";

  const studentPdfUrl =
    attempt?.upload?.url ||
    attempt?.answerPdfUrl ||
    attempt?.submissionPdfUrl ||
    attempt?.uploadedPdfUrl ||
    null;

  return (
    <div className="p-2 p-sm-3">
      <ToastContainer containerId={TOAST_ID} position="bottom-right" newestOnTop closeOnClick={false} theme="dark" limit={4} />

      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-1">{exam.name || "Exam"}</h5>
          <div className="small text-muted">
            {isAnytime ? (
              <>Any time{exam.durationMins ? ` • ${exam.durationMins} mins` : ""}</>
            ) : (
              <>
                {exam.date ? dayjs(exam.date).format("DD MMM YYYY") : "—"}
                {exam.startTime && exam.endTime ? <> • {exam.startTime}–{exam.endTime}</> : null}
                {exam.durationMins ? <> • {exam.durationMins} mins</> : null}
              </>
            )}
          </div>
          <div className="mt-1 d-flex gap-2 align-items-center flex-wrap">
            {exam.status && <Badge bg="success">{String(exam.status).toUpperCase()}</Badge>}
            <Badge bg={isDescriptive ? "warning" : "info"} text={isDescriptive ? "dark" : undefined}>
              {String(exam.examType || "").toUpperCase() || "—"}
            </Badge>
            {!isAnytime && ws.state === "live" && <Badge bg="success">{ws.label}</Badge>}
            {!isAnytime && ws.state === "upcoming" && <Badge bg="info" text="dark">{ws.label}</Badge>}
            {!isAnytime && ws.state === "ended" && <Badge bg="secondary">Ended</Badge>}
            {isAnytime && <Badge bg="success">Available anytime</Badge>}
            {!isActive && <Badge bg="warning" text="dark">Inactive</Badge>}
            {isLiveNow && proctorStrikes > 0 && (
              <Badge bg={proctorStrikes < 3 ? "warning" : "danger"}>
                Proctor warnings: {proctorStrikes}/3
              </Badge>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 justify-content-end">
          {!isDescriptive && (
            <Button variant="primary" onClick={() => nav(`/student/exams/${exam.id}/write`)}>
              Start / Resume Objective Test
            </Button>
          )}
          <Button variant="outline-secondary" onClick={onBack}>Back</Button>
        </div>
      </div>

      {exam.instructions && (
        <Alert variant="info">
          <div className="fw-semibold">Instructions</div>
          <div className="mb-0">{exam.instructions}</div>
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={isDescriptive ? 7 : 12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="fw-semibold">
              Question Paper {loadingQs && <Spinner size="sm" className="ms-2" />}
            </Card.Header>

            <Card.Body
              ref={questionRef}
              onCopy={(e) => { if (isDescriptive) { e.preventDefault(); toast.warn("Copy is disabled during the exam.", { containerId: TOAST_ID }); }}}
              onCut={(e)  => { if (isDescriptive) { e.preventDefault(); }}}
              onContextMenu={(e) => { if (isDescriptive) { e.preventDefault(); }}}
              style={isDescriptive ? { userSelect: "none" } : undefined}
            >
              {isLiveNow && hideQuestions ? (
                <Alert variant="danger" className="mb-0">
                  Proctoring lock: Questions are hidden due to repeated tab switching. Please stay on the exam page.
                </Alert>
              ) : !loadingQs && questions.length === 0 ? (
                <div className="text-muted">
                  No questions found for this paper.
                  <div className="small mt-1">
                    (We checked the central bank, the template’s questions, and the exam’s questions.)
                  </div>
                </div>
              ) : (
                <>
                  {exam.questionPdfUrl && (
                    <>
                      <div className="mb-2">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => window.open(exam.questionPdfUrl, "_blank", "noopener")}
                        >
                          Open Question PDF in new tab
                        </Button>
                      </div>
                      <div className="ratio ratio-4x3 border rounded mb-3">
                        <iframe title="Question PDF" src={exam.questionPdfUrl} style={{ border: 0 }} />
                      </div>
                    </>
                  )}

                  {grouped.length > 0 && (
                    <>
                      {grouped.map((g) => (
                        <div key={g.subject} className="mb-4">
                          <div className="h6 fw-semibold mb-2">Subject: {g.subject}</div>
                          {g.sections.map((s) => (
                            <div key={`${g.subject}-${s.key || "general"}`} className="mb-3">
                              <div className="small text-muted mb-1">
                                {secTitle(s.key)} • Questions {s.start}–{s.end}
                              </div>
                              <ol start={s.start} className="ps-3">
                                {s.items.map((q, idx) => (
                                  <li key={q.id || `${g.subject}-${s.key}-${idx}`} className="mb-4">
                                    <div className="mb-1 d-flex flex-wrap align-items-center gap-2">
                                      {q.marks != null && <Badge bg="light" text="dark">Marks: {q.marks}</Badge>}
                                    </div>

                                    {q.questionHtml ? (
                                      <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.questionHtml }} />
                                    ) : (
                                      <div className="fw-semibold mb-2">
                                        {q.title || q.prompt || q.question || `Question`}
                                      </div>
                                    )}

                                    {qtype(q) === "case_based" && (
                                      <>
                                        {q.passageHtml && (
                                          <div
                                            className="p-2 rounded bg-light mb-2"
                                            dangerouslySetInnerHTML={{ __html: q.passageHtml }}
                                          />
                                        )}
                                        {(q.caseSubQs || []).map((sq, sidx) => (
                                          <div key={sq.id || sidx} className="mb-2">
                                            <div className="fw-semibold small mb-1">Sub-question {sidx + 1}</div>
                                            {sq.questionHtml && (
                                              <div className="mb-1" dangerouslySetInnerHTML={{ __html: sq.questionHtml }} />
                                            )}
                                            {Array.isArray(sq.options) && sq.options.length > 0 && (
                                              <ol type="A" className="mb-1">
                                                {sq.options.map((o, i) => (
                                                  <li key={i}>
                                                    <span dangerouslySetInnerHTML={{
                                                      __html: o?.value || String(o?.label || o?.text || "")
                                                    }}/>
                                                  </li>
                                                ))}
                                              </ol>
                                            )}
                                          </div>
                                        ))}
                                      </>
                                    )}

                                    {["mcq","multi_correct","mcq_descriptive","true_false"].includes(qtype(q)) &&
                                      Array.isArray(q.options) && q.options.length > 0 && (
                                        <ol type="A" className="mt-2 mb-1">
                                          {q.options.map((opt, i) => (
                                            <li key={i}>
                                              <span dangerouslySetInnerHTML={{
                                                __html: opt?.value || String(opt?.label || opt?.text || "")
                                              }}/>
                                            </li>
                                          ))}
                                        </ol>
                                    )}

                                    {Array.isArray(q.images) && q.images.length > 0 && (
                                      <div className="mt-2 d-flex flex-wrap gap-2">
                                        {q.images.map((url, i) => (
                                          <img key={i} src={url} alt="" style={{ maxWidth: 220, borderRadius: 8 }} />
                                        ))}
                                      </div>
                                    )}

                                    {Array.isArray(q.files) && q.files.length > 0 && (
                                      <div className="mt-2 d-flex flex-column">
                                        {q.files.map((f, i) => {
                                          const name = f?.name || f?.fileName || f?.title || "Attachment";
                                          const url  = f?.url || f?.downloadURL || f?.link;
                                          return url ? <a key={i} href={url} target="_blank" rel="noreferrer">{name}</a> : null;
                                        })}
                                      </div>
                                    )}

                                    {/* Per-question solution (obeys LIVE/ANYTIME rules + toggle) */}
                                    {(() => {
                                      if (!canRenderInlineSolutions) return null;
                                      const solHtml =
                                        q.solutionHtml || q.modelAnswerHtml || q.answerHtml || q.explanationHtml || "";
                                      if (!solHtml) return null;
                                      return (
                                        <div className="sol-box mt-2">
                                          <div className="sol-title">Solution</div>
                                          <div dangerouslySetInnerHTML={{ __html: solHtml }} />
                                        </div>
                                      );
                                    })()}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Upload panel (descriptive) */}
        {isDescriptive && (
          <Col lg={5}>
            <Card className="border-0 shadow-sm sticky-side">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <div className="fw-semibold">Upload Answer Sheet (PDF)</div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  {attempt?.evaluatedAt ? (
                    <Badge bg="dark">Evaluated • {fmtShort(toJS(attempt.evaluatedAt))}</Badge>
                  ) : attempt?.submittedAt ? (
                    <Badge bg="secondary">Submitted • {fmtShort(toJS(attempt.submittedAt))}</Badge>
                  ) : null}

                  {/* Big marks chip */}
                  {isDescriptive && hasSubmitted(attempt) && (() => {
                    const s = getDescScore(attempt);
                    const t = getDescTotal(exam, attempt, questions);
                    return (
                      <div className="marks-chip" title={t != null ? `Out of ${t}` : ""}>
                        <div className="mc-top">Marks</div>
                        <div className="mc-value">
                          {s != null ? s : "—"}{t != null ? <span className="mc-total"> / {t}</span> : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </Card.Header>

              <Card.Body>
                {!isActive ? (
                  <Alert variant="warning" className="mb-0">Your account is inactive. Upload is disabled.</Alert>
                ) : policyBlocked ? (
                  <Alert variant="secondary" className="mb-3">You have already submitted. Re-attempt not allowed.</Alert>
                ) : ws.state === "upcoming" ? (
                  <Alert variant="warning" className="mb-3">{ws.label}. Upload opens when the exam starts.</Alert>
                ) : ws.state === "ended" ? (
                  <Alert variant="secondary" className="mb-3">This exam has ended.</Alert>
                ) : null}

                {/* Actions row (follows live/mock rules) */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {studentPdfUrl && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => window.open(studentPdfUrl, "_blank", "noopener")}
                      title="Open your uploaded PDF"
                    >
                      Your Upload
                    </Button>
                  )}

                  {/* Model Answer PDF: LIVE → only after submit; MOCK/ANYTIME → always */}
                  {((isLiveExam && hasSubmitted(attempt)) || (!isLiveExam)) && !!modelAnswerUrl && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => window.open(modelAnswerUrl, "_blank", "noopener")}
                      title="Open model answer PDF"
                    >
                      Model Answer PDF
                    </Button>
                  )}

                  {/* View/Hide Solutions button per rule */}
                  {canShowSolutionsButton && (
                    <Button
                      size="sm"
                      variant={showSolutions ? "outline-success" : "outline-secondary"}
                      onClick={onViewSolutionsTabToggle}
                      title="Show/hide model answers below"
                    >
                      {showSolutions ? "Hide Solutions" : "View Solutions"}
                    </Button>
                  )}

                  {/* Remarks popup */}
                  {isEvaluated(attempt) && attempt?.remarks && (
                    <Button
                      size="sm"
                      variant="outline-dark"
                      onClick={() => setShowRemarks(true)}
                      title="Open full teacher remarks"
                    >
                      View Remarks
                    </Button>
                  )}
                </div>

                {/* Upload controls (enabled only in window) */}
                {!policyBlocked && ws.state !== "ended" && (
                  <>
                    <Form.Group controlId="pdfUpload" className="mb-2">
                      <Form.Label className="small">Select PDF file</Form.Label>
                      <Form.Control type="file" accept="application/pdf" onChange={onChooseFile} disabled={uploading || !canUploadWindow} />
                      <div className="small text-muted mt-1">
                        Max size: {exam?.maxUploadMB || 15} MB {(!isMock && ws.state !== "live") ? "• Upload enabled only during the live window" : ""}
                      </div>
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        className="btn-accent"
                        disabled={!file || uploading || !canUploadWindow}
                        onClick={doUpload}
                        title={!file ? "Choose a PDF file to upload" : undefined}
                      >
                        {uploading ? "Uploading…" : (studentPdfUrl ? "Upload Again" : "Upload PDF")}
                      </Button>
                    </div>
                  </>
                )}

                {/* LIVE-only hint before submission */}
                {isLiveExam && !hasSubmitted(attempt) && anySolutionAttached && (
                  <div className="small text-muted mt-1">
                    Solutions unlock after you upload your PDF.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Teacher Remarks Modal */}
    <Modal show={showRemarks} onHide={() => setShowRemarks(false)} size="lg" centered>
  <Modal.Header closeButton>
    <Modal.Title>Teacher’s Remarks</Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {(() => {
      const s   = getDescScore(attempt);                 // obtained
      const t   = getDescTotal(exam, attempt, questions);// total
      const pct = (t && s != null) ? Math.round((s / t) * 100) : null;
      const grd = gradeOf(pct);

      return attempt?.remarks ? (
        <>
          {/* % + Grade */}
          <div className="d-flex align-items-center gap-2 mb-2">
            {pct != null && <Badge bg="dark">{pct}%</Badge>}
            {grd && <Badge bg={grd === "F" ? "danger" : "success"}>Grade {grd}</Badge>}
            {t != null && s != null && (
              <Badge bg="secondary">{s} / {t}</Badge>
            )}
          </div>

          {/* Plain-text remarks (no HTML injection) */}
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {String(attempt.remarks)}
          </div>

          {isEvaluated(attempt) && attempt?.evaluatedBy?.name && (
            <div className="mt-3 text-muted small">
              — Evaluated by <b>{attempt.evaluatedBy.name}</b>
              {attempt.evaluatedBy.email ? ` (${attempt.evaluatedBy.email})` : ""}
              {attempt?.evaluatedAt ? ` • ${fmtShort(toJS(attempt.evaluatedAt))}` : ""}
            </div>
          )}
        </>
      ) : (
        <div className="text-muted">No remarks added.</div>
      );
    })()}
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowRemarks(false)}>Close</Button>
  </Modal.Footer>
</Modal>


      <style>{`
        .btn-accent { background-color: #4f46e5 !important; border-color: #4f46e5 !important; color: #fff !important; }
        .btn-accent:hover, .btn-accent:focus { filter: brightness(0.92); color: #fff !important; }
        .btn-accent:disabled { opacity: .65 !important; }
        .sticky-side { position: sticky; top: 12px; }

        .sol-box{
          border:1px solid rgba(22,163,74,.18);
          background:linear-gradient(0deg, rgba(16,185,129,.06), rgba(16,185,129,.06));
          border-left:4px solid #16a34a;
          border-radius:10px;
          padding:10px 12px;
          margin-top:.5rem;
        }
        .sol-title{
          font-size:.82rem; font-weight:700; color:#065f46; margin-bottom:.25rem;
          text-transform:uppercase; letter-spacing:.02em;
        }

        .marks-chip{
          min-width: 110px;
          padding: 8px 12px;
          border-radius: 12px;
          background: linear-gradient(180deg, #111827, #1f2937);
          color: #fff;
          text-align: right;
          box-shadow: 0 4px 14px rgba(0,0,0,.18);
        }
        .marks-chip .mc-top{
          font-size: .72rem;
          letter-spacing: .04em;
          text-transform: uppercase;
          opacity: .85;
          margin-bottom: 2px;
        }
        .marks-chip .mc-value{
          font-size: 1.35rem;
          font-weight: 800;
          line-height: 1.1;
        }
        .marks-chip .mc-total{
          font-size: .95rem;
          font-weight: 600;
          opacity: .9;
        }

        @media (max-width: 991.98px){
          .sticky-side{ position: static; }
        }
      `}</style>
    </div>
  );
}
